import { Box, Button, Stack, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { REACT_APP_URL } from "utils/environmentVariables";
import { TIMEZONE_OPTIONS } from "utils/constants";
import { selectCurrentUserInfo } from "app/containers/Global/slice";
import { selectCompanySettings } from "app/containers/AdminConsole/slice";
import {
  selectUpdateUserTimezoneStatus,
  updateUserTimezone,
} from "app/containers/UserSettings/slice";
import {
  getCoachBoAvailableLanguages,
  selectAvailableCoachBoLanguages,
  selectCurrentChatbotLanguage,
  updateUserCoachBoLanguage,
} from "app/containers/Chatbot/slice";
import { useRightPanel } from "../../../common";
import { MediumCustomIcon } from "../../layer00";
import {
  RIGHT_PANEL_GUTTER,
  RightPanelLayout,
  SettingsField,
  SettingsSection,
} from "../../layer01";

type SectionKey = "language" | "timezone";

const timezoneOptions = TIMEZONE_OPTIONS.map((timezone) => ({
  value: timezone.tzCode,
  label: timezone.label,
}));

export const SettingsPanel = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const panel = useRightPanel();

  const currentUserInfo = useAppSelector(selectCurrentUserInfo);
  const companySettings = useAppSelector(selectCompanySettings);
  const availableLanguages = useAppSelector(selectAvailableCoachBoLanguages);
  const currentLanguage = useAppSelector(selectCurrentChatbotLanguage);
  const updateUserTimezoneStatus = useAppSelector(
    selectUpdateUserTimezoneStatus
  );

  const [expandedSection, setExpandedSection] = useState<
    SectionKey | undefined
  >("language");

  useEffect(() => {
    dispatch(getCoachBoAvailableLanguages());
  }, [dispatch]);

  const languageOptions = useMemo(() => {
    // Keep the current language selectable while the list is still loading.
    const languages = availableLanguages.includes(currentLanguage)
      ? availableLanguages
      : [currentLanguage, ...availableLanguages];
    return languages.map((language) => ({ value: language, label: language }));
  }, [availableLanguages, currentLanguage]);

  const currentTimezone =
    currentUserInfo?.timezone ?? companySettings?.timezone ?? null;

  const iconStroke = isDark ? theme.palette.text.secondary : "#334C71";

  const expand = (section: SectionKey) =>
    setExpandedSection((prev) => (prev === section ? undefined : section));

  const openFullWebApp = () => {
    window.open(
      `${REACT_APP_URL}?critToken=${localStorage.getItem("critToken")}`,
      "_blank"
    );
  };

  return (
    <RightPanelLayout title="Settings" handleClose={panel.close}>
      <Box
        borderBottom={`1px solid ${isDark ? theme.palette.divider : "#ECEEF9"}`}
      >
        <SettingsSection
          label="Preferred language"
          isExpanded={expandedSection === "language"}
          onToggle={() => expand("language")}
        >
          <SettingsField
            label="Bo's language"
            value={currentLanguage}
            options={languageOptions}
            onChange={(language) =>
              dispatch(updateUserCoachBoLanguage(language))
            }
            icon={
              <MediumCustomIcon
                icon="SPEECHBUBBLE"
                stroke={iconStroke}
                width={20}
                height={20}
              />
            }
          />
        </SettingsSection>

        <SettingsSection
          label="Your timezone"
          isExpanded={expandedSection === "timezone"}
          onToggle={() => expand("timezone")}
        >
          <SettingsField
            label="Set timezone"
            value={currentTimezone}
            options={timezoneOptions}
            placeholder="Search timezone"
            onChange={(tzCode) => dispatch(updateUserTimezone(tzCode))}
            disabled={updateUserTimezoneStatus === "loading"}
            icon={
              <MediumCustomIcon
                icon="LOCATION"
                stroke={iconStroke}
                width={20}
                height={20}
              />
            }
          />
        </SettingsSection>
      </Box>

      <Stack marginTop="auto" paddingY="1rem" paddingX={RIGHT_PANEL_GUTTER}>
        <Button
          variant="outlined"
          color="primary"
          onClick={openFullWebApp}
          startIcon={<MediumCustomIcon icon="MONITOR" />}
        >
          Full web-app
        </Button>
      </Stack>
    </RightPanelLayout>
  );
};
